import React from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, X } from "lucide-react";

export const filterActive = (col, v) => {
  if (!v) return false;
  if (col.type === "text") return !!v.trim();
  if (col.type === "options") return v.length > 0;
  return !!(v.from || v.to || v.min || v.max);
};

export function FilterControl({ def, value, onChange, options, testPrefix }) {
  const opts = def.options || options || [];
  if (def.type === "text") {
    return <Input className="h-8 text-sm" placeholder="Contains…" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testPrefix}-filter-input-${def.key}`} />;
  }
  if (def.type === "daterange") {
    return (
      <div className="space-y-2">
        <input type="date" className="w-full h-8 px-2 rounded-md border border-summit-hairline text-xs" value={value?.from || ""} onChange={(e) => onChange({ ...value, from: e.target.value })} data-testid={`${testPrefix}-filter-from-${def.key}`} />
        <input type="date" className="w-full h-8 px-2 rounded-md border border-summit-hairline text-xs" value={value?.to || ""} onChange={(e) => onChange({ ...value, to: e.target.value })} data-testid={`${testPrefix}-filter-to-${def.key}`} />
      </div>
    );
  }
  if (def.type === "numrange") {
    return (
      <div className="flex gap-2">
        <Input className="h-8 text-sm" type="number" placeholder="Min" value={value?.min || ""} onChange={(e) => onChange({ ...value, min: e.target.value })} data-testid={`${testPrefix}-filter-min-${def.key}`} />
        <Input className="h-8 text-sm" type="number" placeholder="Max" value={value?.max || ""} onChange={(e) => onChange({ ...value, max: e.target.value })} data-testid={`${testPrefix}-filter-max-${def.key}`} />
      </div>
    );
  }
  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto">
      {opts.map((o) => {
        const sel = (value || []).includes(o);
        return (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`${testPrefix}-filter-opt-${def.key}-${String(o).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <Checkbox checked={sel} onCheckedChange={(c) => onChange(c ? [...(value || []), o] : (value || []).filter((x) => x !== o))} />
            <span className="capitalize">{o}</span>
          </label>
        );
      })}
      {!opts.length && <p className="text-xs text-gray-400">No values yet.</p>}
    </div>
  );
}

export function HeaderFilterPopover({ col, filterDefs, filters, setFilter, nameOptions, testPrefix }) {
  const defs = col.keys.map((k) => filterDefs.find((d) => d.key === k));
  const active = defs.some((d) => filterActive(d, filters[d.key]));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`ml-1 p-0.5 rounded align-middle ${active ? "text-summit-primary bg-summit-glacier" : "text-gray-300 hover:text-gray-500"}`}
          data-testid={`${testPrefix}-filter-${col.keys[0]}`}
          title={`Filter ${col.label}`}
        >
          <Filter className="w-3 h-3" fill={active ? "currentColor" : "none"} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 space-y-3" align="start">
        {defs.map((def) => (
          <div key={def.key}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Filter · {def.label}</span>
              {filterActive(def, filters[def.key]) && (
                <button className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-600" onClick={() => setFilter(def.key, undefined)} data-testid={`${testPrefix}-filter-clear-${def.key}`}>
                  <X className="w-3 h-3" /> clear
                </button>
              )}
            </div>
            <FilterControl def={def} value={filters[def.key]} onChange={(v) => setFilter(def.key, v)} options={nameOptions?.[def.key]} testPrefix={testPrefix} />
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
